import React, { JSX } from "react";
import { DashboardPriority } from "../mockData";

type Props = {
  priorities: DashboardPriority[];
};

const statusStyles: Record<DashboardPriority["status"], string> = {
  critical: "bg-rose-100 text-rose-700 border-rose-200",
  attention: "bg-amber-100 text-amber-700 border-amber-200",
  stable: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

const statusIcons: Record<DashboardPriority["status"], JSX.Element> = {
  critical: (
    <svg
      className="w-4 h-4 text-rose-600 mr-1"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="12" r="10" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01" />
    </svg>
  ),
  attention: (
    <svg
      className="w-4 h-4 text-amber-500 mr-1"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="12" r="10" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l2 2" />
    </svg>
  ),
  stable: (
    <svg
      className="w-4 h-4 text-emerald-600 mr-1"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="12" r="10" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" />
    </svg>
  ),
};

const getBorderColor = (status: DashboardPriority["status"]) => {
  switch (status) {
    case "critical":
      return "border-l-rose-500";
    case "attention":
      return "border-l-amber-500";
    case "stable":
      return "border-l-emerald-500";
    default:
      return "border-l-gray-500";
  }
};

const DashboardPriorityPanel: React.FC<Props> = ({ priorities }) => {
  return (
    <section className="rounded-xl bg-white shadow-lg flex flex-col h-full">
      <div className="border-b border-gray-100 px-6 py-5">
        <h2 className="text-xl font-bold text-black">Complaint Priorities</h2>
        <p className="mt-1 text-sm text-gray-600">
          Items requiring immediate attention
        </p>
      </div>

      <div className="space-y-5 p-5 flex-1 flex flex-col">
        {priorities.map((priority) => (
          <article
            key={priority.title}
            className={`rounded-xl border border-gray-100 bg-white p-5 shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-1 ${getBorderColor(
              priority.status,
            )} border-l-4`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-bold text-black">
                  {priority.title}
                </h3>
                <p className="mt-1 text-xs uppercase tracking-wide font-medium text-gray-600">
                  {priority.owner}
                </p>
              </div>
              <span
                className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold capitalize ${statusStyles[priority.status]}`}
              >
                {statusIcons[priority.status]}
                {priority.status}
              </span>
            </div>
            <p className="mt-4 text-3xl font-extrabold text-black">
              {priority.count}
            </p>
            <p className="mt-2 text-sm leading-6 text-gray-700">
              {priority.note}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
};

export default DashboardPriorityPanel;
